import type { Cap } from '@artmozaik/shared';
import type { IkonAd } from './Ikon';

/**
 * Sol menünün TEK kaynağı: kabuk (Shell) ve ⌘K araması aynı ağacı okur.
 * Yollar ve yetkiler API'deki kurallarla aynıdır; burada yalnız yer ve ad tutulur.
 *
 * `yoksa`: kullanıcıda bu yetki VARSA öğe gizlenir (aynı ekranın daha geniş hâli zaten menüdedir).
 * `tam`: yalnız tam eşleşmede etkin görünür (alt sayfası ayrı bir menü öğesidir).
 */
export type RozetAd = 'teklif' | 'hatirlatma' | 'cek' | 'destek';
export type AltOge = { to: string; label: string; cap: Cap; yoksa?: Cap; tam?: boolean; rozet?: RozetAd };
export type AnaOge = {
  id: string; label: string; ikon: IkonAd;
  /** Kendi sayfası. Yoksa (Raporlar) tıklayınca yetkisi olan ilk alt sayfaya gidilir. */
  to?: string; cap?: Cap; yoksa?: Cap; tam?: boolean;
  /** Alt öğeleri olsa bile yalnız kendi yetkisi varsa görünür ("Faturasız Çıkış" çeşidi). */
  yalnizKendi?: boolean;
  alt?: AltOge[]; rozet?: RozetAd;
};

export const ANA_MENU: AnaOge[] = [
  { id: 'ozet', to: '/', label: 'Genel Özet', ikon: 'ev', cap: 'dashboard.view' },
  { id: 'teklif', to: '/teklif', label: 'Teklifler', ikon: 'belge', cap: 'quote.view', rozet: 'teklif' },
  { id: 'siparis', to: '/siparis', label: 'Sipariş Hattı', ikon: 'akis', cap: 'order.view' },
  // CRM: SAHA rolünde `quote.view` yok; dal hiç görünmez.
  { id: 'cari', to: '/crm', label: 'Cariler', ikon: 'kisi', cap: 'quote.view', alt: [
    { to: '/bugun', label: 'Bugün', cap: 'quote.view' },
    { to: '/numune', label: 'Numuneler', cap: 'quote.view' },
  ] },
  { id: 'stok', to: '/stok', label: 'Ürünler ve Stok', ikon: 'kutu', cap: 'stock.view', alt: [
    { to: '/alis', label: 'Alış Faturaları', cap: 'purchase.view' },
    { to: '/fason', label: 'Fason İşler', cap: 'fason.view' },
  ] },
  // `tam`: alt sayfası (Borç Hatırlatma) açıkken Tahsilat etkin görünmesin.
  { id: 'tahsilat', to: '/tahsilat', label: 'Tahsilat', ikon: 'lira', cap: 'receivable.view', tam: true, rozet: 'hatirlatma', alt: [
    { to: '/tahsilat/hatirlatma', label: 'Borç Hatırlatma', cap: 'reminder.view', rozet: 'hatirlatma' },
  ] },
  // SAHA: havuzu göremez ama çıkışı girer; kendi menü öğesi.
  { id: 'faturasiz', to: '/tahsilat', label: 'Faturasız Çıkış', ikon: 'lira', cap: 'informal.write', yoksa: 'receivable.view', tam: true, yalnizKendi: true, rozet: 'hatirlatma', alt: [
    { to: '/tahsilat/hatirlatma', label: 'Borç Hatırlatma', cap: 'reminder.view', rozet: 'hatirlatma' },
  ] },
  { id: 'kasa', to: '/kasa', label: 'Kasa ve Banka', ikon: 'kasa', cap: 'cash.view', tam: true, rozet: 'cek', alt: [
    { to: '/cek-senet', label: 'Çek / Senet', cap: 'cheque.view', rozet: 'cek' },
    { to: '/kredi', label: 'Krediler', cap: 'loan.view' },
    { to: '/kasa/nakit-akisi', label: 'Nakit Akışı', cap: 'cash.view' },
  ] },
  // Raporlar: dönem süzgeçli sayfalar; yalnız parayı görebilen role (API'de de `receivable.view`).
  { id: 'rapor', label: 'Raporlar', ikon: 'grafik', alt: [
    { to: '/rapor/satis', label: 'Satış', cap: 'receivable.view' },
    { to: '/rapor/musteriler', label: 'Müşteriler', cap: 'receivable.view' },
    { to: '/rapor/urunler', label: 'Ürünler', cap: 'receivable.view' },
    { to: '/rapor/finans', label: 'Finans', cap: 'receivable.view' },
    { to: '/rapor/tahsilat-riski', label: 'Tahsilat Riski', cap: 'receivable.view' },
    { to: '/rapor/siparisler', label: 'Siparişler', cap: 'receivable.view' },
    { to: '/rapor/temsilciler', label: 'Temsilciler', cap: 'receivable.view' },
    { to: '/rapor/stok', label: 'Stok ve Maliyet', cap: 'quote.cost' },
    { to: '/finans', label: 'Tanımlar ve Bakiyeler', cap: 'receivable.view' },
  ] },
  // Kamuya açık sitenin yönetimi: yalnız yönetici (API'de de `settings.write`).
  { id: 'site', to: '/site', label: 'Site', ikon: 'site', cap: 'settings.write', tam: true, alt: [
    { to: '/site/ozellestir', label: 'Özelleştir', cap: 'settings.write' },
    { to: '/site/blog', label: 'Blog yazıları', cap: 'settings.write' },
    { to: '/site/projeler', label: 'Projeler', cap: 'settings.write' },
    { to: '/site/gorseller', label: 'Görseller', cap: 'settings.write' },
    { to: '/site/ayarlar', label: 'Site ayarları', cap: 'settings.write' },
    { to: '/site/yayin-gecmisi', label: 'Yayın geçmişi', cap: 'settings.write' },
  ] },
];

/** Altta sabit duran küçük öğeler. Herkes kendi açtığı destek talebini ve yanıtını görebilmeli. */
export const IKINCIL_MENU: AnaOge[] = [
  { id: 'donem', to: '/donem', label: 'Dönemler', ikon: 'takvim', cap: 'period.view' },
  { id: 'kayit-izi', to: '/kayit-izi', label: 'Kayıt İzi', ikon: 'saat', cap: 'audit.view' },
  { id: 'destek', to: '/destek', label: 'Destek Talepleri', ikon: 'soru', cap: 'dashboard.view', rozet: 'destek' },
];

export const AYARLAR_OGE: AnaOge = { id: 'ayarlar', to: '/ayarlar', label: 'Ayarlar', ikon: 'ayar', cap: 'settings.view' };

type Can = (c: Cap) => boolean;
const izinli = (o: { cap?: Cap; yoksa?: Cap }, can: Can) => (!o.cap || can(o.cap)) && !(o.yoksa && can(o.yoksa));

export type GorunenOge = AnaOge & { kendi: boolean; altlar: AltOge[]; hedef: string };

/**
 * Role göre süzülmüş menü. Kendi sayfasına da alt sayfalarının hiçbirine de yetkisi
 * olmayan ana öğe görünmez; kendi sayfası yoksa (ya da yetkisi yoksa) ilk alt sayfaya gider.
 */
export function gorunenMenu(liste: AnaOge[], can: Can): GorunenOge[] {
  const sonuc: GorunenOge[] = [];
  for (const o of liste) {
    const kendi = Boolean(o.to) && izinli(o, can);
    if (o.yalnizKendi && !kendi) continue;
    // `yoksa` çeşidi: geniş hâli menüdeyse bu öğe hiç çıkmaz (alt öğeleri öbüründe zaten var).
    if (o.yoksa && can(o.yoksa)) continue;
    const altlar = (o.alt ?? []).filter((a) => izinli(a, can));
    if (!kendi && altlar.length === 0) continue;
    // Kendi sayfası yok, tek alt sayfası var: kırılım açmak yerine o sayfa doğrudan menü öğesi olur
    // (ör. yalnız Borç Hatırlatma'yı gören rolde "Tahsilat" başlığı çıkmaz).
    if (!kendi && altlar.length === 1) {
      const a = altlar[0];
      sonuc.push({ ...o, label: a.label, to: a.to, cap: a.cap, tam: a.tam, rozet: a.rozet, kendi: true, altlar: [], hedef: a.to });
      continue;
    }
    sonuc.push({ ...o, kendi, altlar, hedef: kendi ? o.to! : altlar[0].to });
  }
  return sonuc;
}

export const yolEslesir = (pathname: string, to: string, tam?: boolean) =>
  pathname === to || (!tam && to !== '/' && pathname.startsWith(`${to}/`));

/** Adrese göre etkin dal ve (varsa) etkin alt öğe. En uzun eşleşen alt öğe kazanır. */
export function etkinDal(menu: GorunenOge[], pathname: string): { id: string; alt: string | null } | null {
  let enIyi: { id: string; alt: string; uz: number } | null = null;
  for (const o of menu) for (const a of o.altlar) {
    if (yolEslesir(pathname, a.to, a.tam) && (!enIyi || a.to.length > enIyi.uz)) enIyi = { id: o.id, alt: a.to, uz: a.to.length };
  }
  if (enIyi) return { id: enIyi.id, alt: enIyi.alt };
  const o = menu.find((x) => x.kendi && yolEslesir(pathname, x.to!, x.tam));
  return o ? { id: o.id, alt: null } : null;
}

/* ─── Telefon alt gezinme çubuğu ──────────────────────────────────────────────
   Role göre en çok kullanılan dört ekran + "Menü". Yetkiler yukarıdaki menü ağacından
   okunur (ikinci bir yetki listesi YOK): bir ekran menüde görünmüyorsa çubukta da görünmez. */
export type AltCubukOge = { to: string; label: string; ikon: IkonAd; rozet?: RozetAd; tam?: boolean };

type Aday = { to: string; label: string; ikon: IkonAd; rozet?: RozetAd; tam?: boolean; ana?: string };
const ADAY: Record<string, Aday> = {
  bugun: { to: '/bugun', label: 'Bugün', ikon: 'bugun' },
  ozet: { to: '/', label: 'Özet', ikon: 'ev', tam: true },
  stok: { to: '/stok', label: 'Stok', ikon: 'kutu' },
  teklif: { to: '/teklif', label: 'Teklif', ikon: 'belge', rozet: 'teklif' },
  tahsilat: { to: '/tahsilat', label: 'Tahsilat', ikon: 'lira', rozet: 'hatirlatma', ana: 'tahsilat' },
  // "Çıkış" tek başına oturumu kapatma düğmesiyle karışır: etiket "Faturasız".
  faturasiz: { to: '/tahsilat', label: 'Faturasız', ikon: 'lira', ana: 'faturasiz' },
  cari: { to: '/crm', label: 'Cariler', ikon: 'kisi' },
  siparis: { to: '/siparis', label: 'Sipariş', ikon: 'akis' },
  rapor: { to: '/rapor/satis', label: 'Raporlar', ikon: 'grafik' },
  kasa: { to: '/kasa', label: 'Kasa', ikon: 'kasa', rozet: 'cek', tam: true },
  fason: { to: '/fason', label: 'Fason', ikon: 'numune' },
  alis: { to: '/alis', label: 'Alış', ikon: 'fatura' },
};
/** Öncelik sırası; yetkisi olmayanlar atlanır, ilk dördü çubuğa girer. */
const ALT_SIRA: Record<string, string[]> = {
  ADMIN: ['bugun', 'stok', 'teklif', 'tahsilat', 'ozet', 'siparis'],
  OPERASYON: ['bugun', 'stok', 'teklif', 'tahsilat', 'ozet', 'siparis'],
  SATIS: ['bugun', 'stok', 'teklif', 'cari', 'siparis', 'ozet'],
  SAHA: ['stok', 'faturasiz', 'bugun', 'siparis', 'fason', 'ozet'],
  MUHASEBE_RO: ['ozet', 'tahsilat', 'rapor', 'kasa', 'stok', 'alis'],
};

/** Menü ağacında bu adrese (ve istenirse bu ana öğeye) yetkisi olan bir giriş var mı? */
function menudeVar(a: Aday, gorunen: GorunenOge[]): boolean {
  for (const o of gorunen) {
    if (a.ana && o.id !== a.ana) continue;
    if (o.kendi && o.to === a.to) return true;
    if (o.altlar.some((x) => x.to === a.to)) return true;
  }
  return false;
}

export function altCubuk(rol: string, can: Can): AltCubukOge[] {
  const gorunen = gorunenMenu(ANA_MENU, can);
  const sira = ALT_SIRA[rol] ?? ALT_SIRA.ADMIN;
  const sonuc: AltCubukOge[] = [];
  for (const ad of sira) {
    const a = ADAY[ad];
    if (!a || sonuc.some((x) => x.to === a.to) || !menudeVar(a, gorunen)) continue;
    sonuc.push({ to: a.to, label: a.label, ikon: a.ikon, rozet: a.rozet, tam: a.tam });
    if (sonuc.length === 4) break;
  }
  return sonuc;
}
